import * as React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useDrawer } from './useDrawer'
import { componentRoutes } from 'router/routes'
import { isAccessDenied } from 'router/isAccessDenied'

export interface LeftSideMenuBarProps {
  readonly children?: React.ReactNode
  readonly permissions: readonly string[]
}

const colors = {
  sideBarBackground: '#0D3349',
  toggleBackground: '#0B4364',
  textColor: '#DDDDDD',
  dropShadow: '#00000029'
}

const offsets = {
  expandedSideBarWidth: 200,
  collapsedSideBarWidth: 40,
  navMarginLeft: -15,
  contentExtraPaddingLeft: 40
}

const ToggleButton = styled.i`
  border-radius: 60px;
  box-shadow: -4px 0 3px 2px ${colors.dropShadow};
  padding: 0.4em 0.6em;
  color: ${colors.textColor};
  float: right;
  margin-top: 10px;
  margin-right: -10px;
  background-color: ${colors.toggleBackground};
`

const Nav = styled.nav`
  // Set a high z-index here to override the default stacking order
  // which is (bottom) first element -> (top) last element.
  // Needs to also be less than modals.
  z-index: 999;
  box-shadow: inset 0 5px 3px -3px ${colors.dropShadow};
  height: 100%;
  background-color: ${colors.sideBarBackground};
  position: fixed;
  top: 60px;
  left: 0;
  margin-left: ${offsets.navMarginLeft}px;

  li.expanded {
    width: ${offsets.expandedSideBarWidth}px;
    transition: linear 0.2s;
  }

  li.collapsed {
    width: ${offsets.collapsedSideBarWidth}px;
    transition: linear 0.2s;
  }

  @media (max-width: 1024px) {
    top: 60px !important;
  }
`

const StyledUl = styled.ul`
  list-style-type: none;
  align-items: stretch;
  margin-top: -30px;
`

const StyledLi = styled.li`
  color: ${colors.textColor};
  padding-top: 5px;
  padding-bottom: 10px;
  overflow: hidden;
  white-space: nowrap;
  user-select: none;
`

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  align-items: stretch;

  div.content.expanded {
    padding-left: ${offsets.expandedSideBarWidth +
    offsets.contentExtraPaddingLeft}px;

    transition: linear 0.2s;
  }

  div.content.collapsed {
    padding-left: ${offsets.collapsedSideBarWidth +
    offsets.contentExtraPaddingLeft}px;
    transition: linear 0.2s;
  }
`

const StyledLink = styled(Link)`
  color: ${colors.textColor};
  display: table;
  table-layout: fixed;
  width: ${offsets.expandedSideBarWidth}px;
  font-weight: bolder;
  &:hover {
    text-decoration: none;
    color: #ffffff;
  }
`

/**
 * This component has overlapping elements which have onClick handlers.
 * We only want the click event to be handled by one of these click handlers,
 * otherwise annoying behaviour will result.
 * @param onClick
 * @returns MouseEventHandler | KeyboardEventHandler
 */
function onClickNoBubbling(
  onClick: () => void
): (e: React.KeyboardEvent | React.MouseEvent) => void {
  return (e): void => {
    e.stopPropagation()
    onClick()
  }
}

export function LeftSideMenuBar({
  children,
  permissions
}: LeftSideMenuBarProps): JSX.Element {
  const [
    { expanded, pinned },
    { onMouseEnter, onMouseLeave, onTogglePinned }
  ] = useDrawer(true)

  const drawerState = `${expanded ? 'expanded' : 'collapsed'}`

  return (
    <Wrapper>
      <Nav
        onMouseLeave={onMouseLeave}
        onMouseEnter={onMouseEnter}
        onClick={onTogglePinned}
      >
        <ToggleButton
          onClick={onClickNoBubbling(onTogglePinned)}
          className={pinned ? 'fa fa-angle-right' : 'fa fa-angle-left'}
        />
        <StyledUl>
          {componentRoutes
            .filter(({ permissionWhitelist, shouldHideIconIfAccessDenied }) => {
              return (
                !shouldHideIconIfAccessDenied ||
                !isAccessDenied(permissions, permissionWhitelist)
              )
            })
            .map(({ iconClass, route, title }) => (
              <StyledLi className={drawerState} key={`menu_item _${iconClass}`}>
                {expanded ? (
                  <StyledLink
                    to={route}
                    onClick={(e): void => e.stopPropagation()}
                  >
                    <i
                      className={iconClass}
                      style={{ display: 'table-cell', width: '23px' }}
                    />
                    <span
                      style={{
                        display: 'table-cell',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {title}
                    </span>
                  </StyledLink>
                ) : (
                  <i className={iconClass} />
                )}
              </StyledLi>
            ))}
        </StyledUl>
      </Nav>
      <div className={`content ${drawerState}`}>{children}</div>
    </Wrapper>
  )
}
